import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listMyBookings, getMyScore, getMyProfile,
  AGREEMENT_LABEL, SCORE_COMPONENTS,
  type AgreementRow, type ArtistScore, type ArtistProfile,
} from '../../lib/artist';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, formatDate } from '../../lib/format';
import './ArtistPages.css';

/**
 * Artist dashboard.
 *
 * Ordered by what needs an answer. Incoming requests sit at the top because they
 * are the only thing here with a deadline attached; confirmed dates and the
 * score are reference.
 *
 * The score section is the part worth being careful with. The engine withholds
 * the number until five performed events and sends `buildingTrackRecord`
 * instead — rendering that state as a zero would tell a new artist they are
 * rated badly when in fact they are not rated at all. So the components are
 * still shown (they are real, and worth watching), but the overall number is
 * replaced with the threshold and how far off it is.
 */

const PENDING = 'pending';
const CONFIRMED = new Set(['signed', 'deposit_paid']);

function pct(x: number) {
  return `${Math.round(x * 100)}%`;
}

function ScoreBreakdown({ score }: { score: ArtistScore }) {
  const remaining = score.visibilityThreshold - score.totalEventsPerformed;

  return (
    <section className="art-section">
      <div className="art-section__head">
        <h2 className="art-section__title">Reliability score</h2>
        <span className="art-hint">
          {score.lastCalculatedAt ? `Updated ${formatDate(score.lastCalculatedAt)}` : 'Not yet calculated'}
        </span>
      </div>

      <div className="art-card">
        <div className="art-score-head">
          <div className="art-score-dial">
            {score.buildingTrackRecord ? (
              <>
                <span className="art-score-dial__value">
                  {score.totalEventsPerformed}<span className="art-muted">/{score.visibilityThreshold}</span>
                </span>
                <span className="art-score-dial__label">events performed</span>
              </>
            ) : (
              <>
                <span className="art-score-dial__value">{score.overallScore}</span>
                <span className="art-score-dial__label">out of 100</span>
              </>
            )}
          </div>

          <div className="art-section__note">
            {score.buildingTrackRecord ? (
              <>
                <strong>Building track record.</strong> Your score stays private until you
                have performed {score.visibilityThreshold} events  | {' '}
                {remaining === 1 ? 'one more' : `${remaining} more`} to go. Organizers see
                “building track record”, not a low number. The components below are already
                being tracked.
              </>
            ) : (
              <>Visible to organizers browsing artists. Weighted from the six components
                below; a component with no data yet is dropped and the rest reweighted.</>
            )}
          </div>
        </div>

        <div className="art-components">
          {SCORE_COMPONENTS.map((c) => {
            const raw = score.breakdown[c.key];
            // Four of these are rates in 0..1 and two are 1–5 scores. Normalising the
            // stars to a fraction for the bar keeps the bars comparable, while the
            // printed value stays in the component's own units.
            const fraction = c.kind === 'stars' ? Math.max(0, (raw - 1) / 4) : raw;
            const weight = score.weights[c.weightKey];
            return (
              <div key={c.key} className="art-component">
                <div className="art-component__head">
                  <span className="art-component__label">{c.label}</span>
                  <span className="art-component__weight">
                    <span className="art-component__value">
                      {c.kind === 'stars' ? `${raw.toFixed(1)} / 5` : pct(raw)}
                    </span>
                    {'  ·  '}{Math.round(weight * 100)}% of score
                  </span>
                </div>
                <div className="art-meter">
                  <div
                    className={`art-meter__fill${weight < 0.15 ? ' art-meter__fill--light' : ''}`}
                    style={{ width: `${Math.min(100, Math.max(0, fraction * 100))}%` }}
                  />
                </div>
                <p className="art-component__detail">{c.detail}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BookingRow({ a, action }: { a: AgreementRow; action?: boolean }) {
  return (
    <article className={`art-card${action ? ' art-card--action' : ''}`}>
      <div className="art-row">
        <div className="art-row__main">
          <span className="art-row__title">{a.event_title}</span>
          <span className="art-row__meta">
            {a.event_date ? formatDate(a.event_date) : 'Date TBC'}
            {a.performance_start_time ? ` · ${a.performance_start_time}` : ''}
            {a.set_duration_minutes ? ` · ${a.set_duration_minutes} min set` : ''}
          </span>
          <span className="art-hint mono">{a.contract_number}</span>
        </div>
        <div className="art-row__side">
          <span className="art-money">{formatMoney(a.performance_fee_cents)}</span>
          <span className={`art-badge art-badge--${a.status}`}>{AGREEMENT_LABEL[a.status]}</span>
          <Link className="button button--secondary" to={`/artist/bookings/${a.id}`}>
            {action ? 'Review request' : 'View'}
          </Link>
        </div>
      </div>
    </article>
  );
}

export function ArtistDashboard() {
  const [bookings, setBookings] = useState<AgreementRow[] | null>(null);
  const [score, setScore] = useState<ArtistScore | null>(null);
  const [profile, setProfile] = useState<ArtistProfile | null>(null);
  const [noProfile, setNoProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getMyProfile();
        if (cancelled) return;
        setProfile(p);
        if (!p) {
          // Registering as an artist creates the user but no artist_profiles row,
          // so this is the ordinary first-run state, not an error.
          setNoProfile(true);
          setLoading(false);
          return;
        }
        const [b, s] = await Promise.all([listMyBookings(), getMyScore()]);
        if (cancelled) return;
        setBookings(b);
        setScore(s);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load your dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="art-status">Loading…</p>;

  if (noProfile) {
    return (
      <div className="art-page art-page--narrow">
        <header className="art-header">
          <div>
            <h1 className="art-title">Set up your artist profile</h1>
            <p className="art-lede">
              Your account exists, but you do not have an artist profile yet, organizers
              cannot find or book you until you create one.
            </p>
          </div>
        </header>
        <div className="art-card">
          <p className="art-section__note">
            After you save it, a Voleto admin reviews and approves the profile. Booking
            requests can only be sent to approved artists.
          </p>
          <div className="art-actions">
            <Link className="button button--primary" to="/artist/profile">Create profile</Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) return <p className="art-status art-error" role="alert">{error}</p>;

  const rows = bookings ?? [];
  const incoming = rows.filter((a) => a.status === PENDING && !a.artist_signed_at);
  const awaitingOrganizer = rows.filter((a) => a.status === PENDING && a.artist_signed_at);
  const confirmed = rows
    .filter((a) => CONFIRMED.has(a.status))
    .sort((a, b) => (a.event_date ?? '').localeCompare(b.event_date ?? ''));

  return (
    <div className="art-page">
      <header className="art-header">
        <div>
          <h1 className="art-title">{profile?.stageName ?? 'Artist'}</h1>
          <p className="art-lede">
            {incoming.length > 0
              ? `${incoming.length} booking request${incoming.length === 1 ? '' : 's'} waiting on you.`
              : 'No requests waiting on you right now.'}
          </p>
        </div>
        <Link className="button button--secondary" to="/artist/profile">Edit profile</Link>
      </header>

      {profile && !profile.approved && (
        <p className="art-flash" role="status">
          <strong>Your profile is awaiting admin approval.</strong> Organizers cannot send
          you booking requests until it is approved, so this dashboard will stay empty
          until then.
        </p>
      )}

      <section className="art-section">
        <div className="art-section__head">
          <h2 className="art-section__title">Incoming requests</h2>
          {incoming.length > 0 && <span className="art-hint">Needs your response</span>}
        </div>
        {incoming.length === 0 ? (
          <p className="art-hint">Nothing awaiting a response.</p>
        ) : (
          <div className="art-list">
            {incoming.map((a) => <BookingRow key={a.id} a={a} action />)}
          </div>
        )}
      </section>

      {awaitingOrganizer.length > 0 && (
        <section className="art-section">
          <h2 className="art-section__title">Signed by you, awaiting the organizer</h2>
          <p className="art-section__note">
            You have signed these. They become confirmed once the organizer countersigns.
          </p>
          <div className="art-list">
            {awaitingOrganizer.map((a) => <BookingRow key={a.id} a={a} />)}
          </div>
        </section>
      )}

      <section className="art-section">
        <div className="art-section__head">
          <h2 className="art-section__title">Upcoming confirmed</h2>
          <Link className="art-breadcrumb" to="/artist/bookings">All bookings →</Link>
        </div>
        {confirmed.length === 0 ? (
          <p className="art-hint">No confirmed dates yet.</p>
        ) : (
          <div className="art-list">
            {confirmed.map((a) => <BookingRow key={a.id} a={a} />)}
          </div>
        )}
      </section>

      {score && <ScoreBreakdown score={score} />}
    </div>
  );
}
